import { useState, useEffect, useCallback, useContext, useRef, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { Validation } from "../../../../../@types/validation.types";
import {
	TextField,
	Paper,
	Grid,
	Button,
	Select,
	MenuItem,
	IconButton,
	InputAdornment,
	Tooltip
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import "date-fns";
import STATES from "../../../../../constants/states";
import { UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";

import { withStyles, Theme } from "@material-ui/core/styles";

import SaveIcon from "@material-ui/icons/Save";
import ClearIcon from "@material-ui/icons/Clear";
import SendIcon from "@material-ui/icons/Send";
import EventIcon from "@material-ui/icons/Event";
import InfoIcon from "@material-ui/icons/Info";
import TransferWithinAStationIcon from "@material-ui/icons/TransferWithinAStation";

import "./addLocation.css";
import "../../../../../globalStyles/paperFormStyles.css";
import CustomHelperTextComponent from "../../../../shared/customHelperTextComponent/CustomHelperTextComponent";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../../../utils/api";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { AddLocationSchema, LocationSchema } from "../../../../../@types/group.types";
import ExtractRouteParams from "../../../../../utils/commonFunctions/extractRouteParams";
import { infoTitleMessage } from "../../../../../constants/infoTitleMessage";

export type FindLocationNumberQuery = {
	group_number: number;
	location_number: number;
};

const mailformat = /^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/;

const LightTooltip = withStyles((theme: Theme) => ({
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		boxShadow: theme.shadows[1],
		maxWidth: 130,
		fontSize: 11
	}
}))(Tooltip);

const AddLocation = (): JSX.Element => {
	const [addLocationForm, setAddLocationForm] = useState<DynamicForm>();
	const [groupInformation, setGroupInformation] = useState<any>();
	const [locationInformation, setLocationInformation] = useState<AddLocationSchema>({
		location_number: null,
		location_name: "",
		location_start_date: "",
		email: "",
		phone_number: null,
		address_line_1: "",
		address_line_2: "",
		city: "",
		state: "",
		ZIP: ""
	});
	const [validation, setValidation] = useState<Validation>({
		locationInformation: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const navigate = useNavigate();
	const [pasted, setPasted] = useState(false);
	const { setDashboardHeader } = useContext(UIContext);
	const queryGroupNumber = useRouteParam("gn");
	const [locationNumberExists, setLocationNumberExists] = useState<boolean>(false);
	const [groupNumber, setGroupNumber] = useState<number>();
	const routeParam = ExtractRouteParams(); // This is to extract the route after ?(Question Mark)

	const findLocation = useDebouncedCallback(async (value: string) => {
		// console.log("Location Number");
		if (value !== "") {
			// console.log("groupNumber", groupNumber);
			const findLocationNumberQuery: FindLocationNumberQuery = {
				group_number: groupNumber as number,
				location_number: value as unknown as number
			};
			const SSN = await api.find.findLocation(findLocationNumberQuery);
			if (SSN === false) {
				setLocationNumberExists(false);
			} else {
				setLocationNumberExists(true);
			}
		} else {
			setLocationNumberExists(false);
		}
	}, 500);

	const getGroup = useCallback(async (group_number: number | null | undefined) => {
		setGroupNumber(group_number as number);
		const response = await trackPromise(api.group.getGroup(group_number));

		// console.log("Get Group", response);
		if (response) {
			setGroupInformation(Object.assign({}, response));
		}
	}, []);

	const handleValidation = useCallback(async () => {
		const { location_name, location_number, email, location_start_date, phone_number, city, ZIP, state } =
			locationInformation;
		_validation.current = {
			...validation,
			locationInformation: {},
			status: "invalid"
		};
		let flag = true;

		if (location_name.length === 0) {
			_validation.current.locationInformation["location_name"] = "Location Name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (location_number === null || location_number === "") {
			_validation.current.locationInformation["location_number"] = "Location number is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (String(location_number).includes(" ")) {
			_validation.current.locationInformation["location_number"] = "Location Number cannot contain space";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (location_start_date === "" || location_start_date.length === 0) {
			_validation.current.locationInformation["location_start_date"] = "Location Start Date is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (email.length !== 0) {
			if (!email.match(mailformat)) {
				_validation.current.locationInformation["email"] = "Valid Email is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}
		if (String(email).includes(" ")) {
			_validation.current.locationInformation["email"] = "Email cannot contain space";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (phone_number !== null) {
			if (String(phone_number).length !== 10) {
				_validation.current.locationInformation["phone_number"] = "Enter valid 10 digit phone number";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}
		if (String(phone_number).includes(" ")) {
			_validation.current.locationInformation["phone_number"] = "Phone Number cannot contain space";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (city.length === 0) {
			_validation.current.locationInformation["city"] = "City is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (state.length === 0) {
			_validation.current.locationInformation["state"] = "State is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (ZIP.length === 0) {
			_validation.current.locationInformation["ZIP"] = "ZIP Code is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (ZIP.length !== 0) {
			const _zip = ZIP.replaceAll("-", "");
			if (_zip.length !== 5 && _zip.length !== 9) {
				_validation.current.locationInformation["ZIP"] = "Enter 5 or 9 digit ZIP Code!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [locationInformation, validation]);

	const handleKeyCheck = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
			let { value } = event.target as HTMLInputElement;
			const { name } = event.target as HTMLInputElement;
			const eventKey = event.key;

			if (name === "ZIP") {
				if (eventKey === "Backspace" || eventKey === "Delete") {
					if (/[-]/.test(value) && value.length === 7) {
						event.preventDefault();
						const index = value.indexOf("-");
						value = value.substring(0, index);
						setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
					}
				}
			}
		},
		[locationInformation]
	);

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>) => {
		const { name } = event.target as HTMLInputElement;
		if (name === "ZIP") {
			setPasted(true);
			setTimeout(() => null, 2000);
		}
	};

	const handleRedirectToGroupLocations = useCallback(() => {
		const pathname = `/employer/locations?${routeParam}`;
		navigate(pathname);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAddLocation = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					const {
						location_number,
						location_name,
						location_start_date,
						email,
						phone_number,
						address_line_1,
						address_line_2,
						city,
						state,
						ZIP
					} = locationInformation;

					const locationStartDate = new Date(location_start_date);

					const locationPayload = {
						group_number: groupNumber,
						location_number,
						location_name,
						location_start_date:
							locationStartDate.getFullYear() +
							"-" +
							(locationStartDate.getMonth() + 1) +
							"-" +
							locationStartDate.getDate(),
						CITY: city,
						STATE: state,
						ZIPCODE: ZIP.replaceAll("-", ""),
						EMAIL: email ? email : null,
						PHONE: phone_number ? phone_number : null,
						ADDR1: address_line_1 ? address_line_1 : null,
						ADDR2: address_line_2 ? address_line_2 : null
					};

					const response: LocationSchema = await trackPromise(api.location.addLocation(locationPayload));

					if (response) {
						// console.log("Response Add Location: ", response);
						alert("Location Added Successfully!");

						handleRedirectToGroupLocations();

						// setTimeout(() => {
						// 	handleRedirectToGroupLocations();
						// }, 2000);
					}
				}
			} catch (error) {
				alert("Add Location Submission Unsuccessful!");
				throw error;
			}
		},
		[groupNumber, handleRedirectToGroupLocations, handleValidation, locationInformation]
	);

	const handleLocationInformationDateChange = useCallback(
		(date: MaterialUiPickersDate, name?: string) => {
			const selectedDate = new Date(date as unknown as string | number);
			const parsedDate =
				selectedDate.getFullYear() + "-" + (selectedDate.getMonth() + 1) + "-" + selectedDate.getDate();
			setLocationInformation(Object.assign({}, locationInformation, { [name as string]: parsedDate }));
		},
		[locationInformation]
	);

	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setLocationInformation(Object.assign({}, locationInformation, { [name]: null }));
		},
		[locationInformation]
	);

	const handleLocationInformationChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let { value } = event.target;
			const { name } = event.target;

			if (name === "location_number") {
				if (!isNaN(value as any)) {
					findLocation(value);
					setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
				} else {
					event.target.value = "";
					event.target.value = locationInformation.location_number
						? (locationInformation.location_number as unknown as string)
						: "";
				}
			} else if (name === "location_name") {
				const alphaExp = /^[a-zA-Z]+$/;
				if (value.match(alphaExp) || value === "") {
					value = value.slice(0, 1).toUpperCase() + value.slice(1);
					setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
				} else if (/\s/g.test(value)) {
					setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
				}
			} else if (name === "phone_number") {
				if (!isNaN(value as any)) {
					if (value.length <= 10) {
						setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
					} else {
						event.target.value = locationInformation.phone_number
							? (locationInformation.phone_number as unknown as string)
							: "";
						alert("Phone Number cannot be greater 10 digit!");
					}
				} else {
					event.target.value = "";
					event.target.value = locationInformation.phone_number
						? (locationInformation.phone_number as unknown as string)
						: "";
				}
			} else if (name === "ZIP") {
				if (!pasted) {
					const _value = Number(value.replaceAll("-", ""));
					if (!isNaN(_value) && value.length <= 10) {
						const zip = value;
						if (zip.length === 6) {
							value = value.substring(0, 5) + "-" + value.substring(5);
						}

						// console.log("ZIP Event", event.target.value);
						setLocationInformation(
							Object.assign({}, locationInformation, {
								[name]: value
							})
						);
					} else {
						event.target.value = "";
						event.target.value = locationInformation.ZIP;
					}
				} else {
					setPasted(false);
					if (!isNaN(value as any)) {
						if (value.length > 9) {
							event.target.value = "";
							event.target.value = locationInformation.ZIP;
							alert("Please enter a valid 5 or 9 digit ZIP!");
						} else {
							// console.log("Inside Number");
							const zip = value;

							if (zip.length >= 6 && zip.length <= 9) {
								value = value.substring(0, 5) + "-" + value.substring(5);
							}

							setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
						}
					} else {
						// console.log("Inside alpha-numeric");
						let zipValue = value.replace(/[^0-9]/g, "").replace(/[^\w\s]/gi, "");
						//console.log("replaced string", zipValue);
						alert("You have pasted alpha-numeric ZIP!");

						if (zipValue.length > 9) {
							event.target.value = "";
							event.target.value = locationInformation.ZIP;
							alert("Please enter a valid 5 or 9 digit ZIP!");
						} else {
							const zip = zipValue;

							if (zip.length >= 6 && zip.length <= 9) {
								zipValue = zipValue.substring(0, 5) + "-" + zipValue.substring(5);
							}

							setLocationInformation(Object.assign({}, locationInformation, { [name]: zipValue }));
						}
					}
				}
			} else setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
		},
		[findLocation, locationInformation, pasted]
	);

	// const handleMoveEmployeePageChange = useCallback(() => {
	// 	navigate("/employer/move-employee");
	// }, [navigate]);

	useEffect(() => {
		setAddLocationForm(
			Object.assign(
				{},
				{
					"Group Information": [
						{
							label: "Group Number",
							name: "group_number",
							value: groupInformation?.group_number,
							onChange: () => null,
							type: "textfield"
						},
						{
							label: "Group Name",
							name: "group_name",
							value: groupInformation?.name,
							onChange: () => null,
							type: "textfield"
						}
					],
					"Location Information": [
						{
							label: "Location Number",
							type: "textfield",
							name: "location_number",
							value: locationInformation?.location_number,
							placeholder: "Enter location number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						},
						{
							label: "Location Name",
							type: "textfield",
							name: "location_name",
							value: locationInformation?.location_name,
							placeholder: "Enter location name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						},
						{
							label: "Location Start Date",
							type: "date",
							name: "location_start_date",
							value: locationInformation?.location_start_date,
							placeholder: "Enter location start date",
							onChange: (date: MaterialUiPickersDate, name?: string) =>
								handleLocationInformationDateChange(date, name)
						},
						{
							label: "Email",
							type: "textfield",
							name: "email",
							value: locationInformation?.email,
							placeholder: "Enter email",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						},
						{
							label: "Phone Number",
							type: "textfield",
							name: "phone_number",
							value: locationInformation?.phone_number,
							placeholder: "Enter phone number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						},
						{
							label: "Address Line 1",
							type: "textfield",
							name: "address_line_1",
							value: locationInformation?.address_line_1,
							placeholder: "Enter address",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						},
						{
							label: "Address Line 2",
							type: "textfield",
							name: "address_line_2",
							value: locationInformation?.address_line_2,
							placeholder: "Enter address",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						},
						{
							label: "City",
							type: "textfield",
							name: "city",
							value: locationInformation?.city,
							placeholder: "Enter city name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						},
						{
							label: "State",
							type: "select",
							name: "state",
							value: locationInformation?.state,
							placeholder: "Select state name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event),
							options: STATES
						},
						{
							label: "ZIP",
							type: "textfield",
							name: "ZIP",
							value: locationInformation?.ZIP,
							placeholder: "Enter ZIP",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleLocationInformationChange(event)
						}
					]
				}
			)
		);
	}, [groupInformation, handleLocationInformationChange, handleLocationInformationDateChange, locationInformation]);

	const handleHyphenInZip = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "-") {
			event.preventDefault();
		}
	};

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.add_location);
	}, [setDashboardHeader]);

	useEffect(() => {
		if (queryGroupNumber) getGroup(queryGroupNumber as number);
	}, [getGroup, queryGroupNumber]);

	useEffect(() => {
		if (locationInformation.location_start_date === null)
			setLocationInformation(Object.assign({}, locationInformation, { location_start_date: "" }));
	}, [locationInformation]);

	// console.log("locationInformation", locationInformation);

	return (
		<div className="add-location-container">
			<div className="pf-action-button-container" id="pf-action-button-container">
				{/* <Button className="button-black" variant="contained">
					<span className="button-label-with-icon">Move Employees</span>
					<span>
						<TransferWithinAStationIcon className="button-icon" />
					</span>
				</Button> */}
				<Button className="button-green" variant="contained" onClick={handleAddLocation}>
					<span className="button-label-with-icon">Submit</span>
					<span>
						<SendIcon className="button-icon" />
					</span>
				</Button>
			</div>
			<Grid container spacing={1} className="pf-grid-container">
				{addLocationForm &&
					Object.entries(addLocationForm).map(([key, value], index) => {
						return (
							<div key={index} className="each-form-container">
								<div className="pf-title">{key}</div>
								<Paper className="pf-paper-container" elevation={3} variant="outlined">
									{value.map((eachProp, index) => {
										const { name } = eachProp;
										return (
											<Grid item key={index} xl={12} lg={12} md={12} sm={12} xs={12}>
												<Grid container spacing={1} className="pf-label">
													<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
														<div
															className={
																eachProp.name === "location_name" ||
																eachProp.name === "location_start_date" ||
																eachProp.name === "location_number" ||
																eachProp.name === "city" ||
																eachProp.name === "state" ||
																eachProp.name === "ZIP"
																	? "pf-label-text required"
																	: "pf-label-text"
															}
														>
															{eachProp.label}
														</div>
													</Grid>
													<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
														{eachProp.type === "textfield" ? (
															<TextField
																className="text-field "
																id="first-name-text"
																style={{ width: "340px", borderRadius: 50 }}
																name={eachProp.name}
																value={eachProp.value}
																placeholder={eachProp.placeholder}
																onChange={eachProp.onChange}
																disabled={
																	name === "group_number" || name === "group_name"
																		? true
																		: false
																}
																InputProps={{
																	endAdornment: (
																		<>
																			{name === "group_number" ||
																			name === "group_name" ? (
																				<InputAdornment position="end">
																					<LightTooltip
																						title={
																							name === "group_number"
																								? infoTitleMessage.group
																								: name === "group_name"
																								? infoTitleMessage.group
																								: ""
																						}
																						placement="right-start"
																						arrow
																					>
																						<InfoIcon
																							style={{
																								color: "#808080",
																								fontSize: "20px",
																								pointerEvents: "unset"
																							}}
																						/>
																					</LightTooltip>
																				</InputAdornment>
																			) : null}
																		</>
																	)
																}}
																onKeyDown={(
																	event: React.KeyboardEvent<HTMLDivElement>
																) => handleKeyCheck(event)}
																onPaste={(
																	event: React.ClipboardEvent<HTMLDivElement>
																) => handlePaste(event)}
																variant="outlined"
																inputProps={{
																	onKeyDown:
																		eachProp.name === "ZIP"
																			? handleHyphenInZip
																			: undefined,
																	readOnly:
																		eachProp.name === "group_number" ||
																		eachProp.name === "group_name"
																			? true
																			: false
																}}
																helperText={
																	validation &&
																	validation.locationInformation[eachProp.name]
																		? validation.locationInformation[eachProp.name]
																		: eachProp.name === "location_number" &&
																		  locationNumberExists
																		? "Location number already exists!"
																		: null
																}
															/>
														) : eachProp.type === "select" ? (
															<>
																<Select
																	id="text-align-options"
																	name={eachProp.name}
																	onChange={eachProp.onChange}
																	style={{
																		pointerEvents: "unset",
																		minWidth: 180,
																		textAlign: "left",
																		overflowY: "visible"
																	}}
																	MenuProps={{
																		style: { zIndex: 35960 }
																	}}
																	inputProps={{ "aria-label": "Without label" }}
																>
																	<MenuItem value="" disabled>
																		Select Value
																	</MenuItem>
																	{eachProp.options?.map((option: string) => {
																		return (
																			<MenuItem key={option} value={option}>
																				{option}
																			</MenuItem>
																		);
																	})}
																</Select>
																{validation && validation.locationInformation ? (
																	<CustomHelperTextComponent
																		errorText={
																			validation.locationInformation["state"]
																		}
																	/>
																) : null}
															</>
														) : eachProp.type === "date" ? (
															<MuiPickersUtilsProvider utils={DateFnsUtils}>
																<KeyboardDatePicker
																	className="from-date-input"
																	id="from-date-input"
																	inputVariant="outlined"
																	label={eachProp.label}
																	placeholder={eachProp.placeholder}
																	name={eachProp.name}
																	format="MM/dd/yyyy"
																	error={false}
																	value={eachProp.value}
																	onChange={(date: MaterialUiPickersDate) =>
																		eachProp.onChange(date, eachProp.name)
																	}
																	InputProps={{
																		startAdornment: (
																			<IconButton
																				onClick={(
																					event: React.MouseEvent<
																						HTMLButtonElement,
																						MouseEvent
																					>
																				) =>
																					handleClearDateField(
																						event,
																						eachProp.name
																					)
																				}
																				style={{ order: 1 }}
																			>
																				<ClearIcon
																					color="disabled"
																					fontSize="small"
																				/>
																			</IconButton>
																		),
																		readOnly: true
																	}}
																	InputLabelProps={{
																		shrink: true
																	}}
																	keyboardIcon={
																		<EventIcon style={{ color: "#7cb342" }} />
																	}
																	KeyboardButtonProps={{
																		"aria-label": "change date"
																	}}
																	style={{
																		pointerEvents: "unset",
																		width: "340px",
																		borderRadius: 50
																	}}
																	helperText={
																		validation &&
																		validation.locationInformation[eachProp.name]
																			? validation.locationInformation[
																					eachProp.name
																			  ]
																			: null
																	}
																/>
															</MuiPickersUtilsProvider>
														) : null}
													</Grid>
												</Grid>
											</Grid>
										);
									})}
								</Paper>
							</div>
						);
					})}
			</Grid>
			<div className="pf-action-button-container" id="pf-action-button-container">
				<Button className="button-green" variant="contained" onClick={handleAddLocation}>
					<span className="button-label-with-icon">Submit</span>
					<span>
						<SendIcon className="button-icon" />
					</span>
				</Button>
			</div>
		</div>
	);
};

export default AddLocation;
